import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarCheck, PhoneCall, PartyPopper, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  "Wedding / Reception",
  "Sangeet / Mehndi",
  "Baby Shower",
  "Birthday",
  "Anniversary",
  "Backyard Party",
  "Corporate Event",
  "Other Celebration",
];

const steps = [
  {
    icon: CalendarCheck,
    title: "We confirm your date",
    text: "You'll hear back within 24 hours with availability and a custom quote.",
  },
  {
    icon: PhoneCall,
    title: "We craft your soundtrack",
    text: "A quick call to understand your vibe, must-plays and do-not-plays.",
  },
  {
    icon: PartyPopper,
    title: "You dance. We handle the rest.",
    text: "Setup, soundcheck, MC duties and a packed floor, all on us.",
  },
];

const packageOptions = [
  "Dream Essential",
  "Dream Signature",
  "Dream Premier",
  "Dream Wedding Experience",
  "Not Sure Yet",
];

const budgetOptions = [
  "Under $1,000",
  "$1,000 to $2,500",
  "$2,500 to $5,000",
  "$5,000+",
  "Prefer to Discuss",
];

const addonOptions = [
  "360° Photo Booth",
  "Cold Sparks",
  "Dancing on the Clouds",
  "Venue Uplighting",
  "Intelligent Lighting",
  "Additional Sound",
  "Ceremony & Cocktail Hour Audio",
  "Dhol & Live Percussion",
  "Baraat Experience",
  "Additional Event Time",
  "Custom Enhancements",
];

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  venue: "",
  guests: "",
  package: "",
  budget: "",
  addons: [],
  message: "",
};

const inputCls =
  "bg-white/5 border-white/10 h-12 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

export default function Booking() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    package: searchParams.get("package") || "",
  }));
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const toggleAddon = (a) =>
    setForm({
      ...form,
      addons: form.addons.includes(a)
        ? form.addons.filter((x) => x !== a)
        : [...form.addons, a],
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_type) {
      toast.error("Please select an event type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Enquiry received! We'll be in touch within 24 hours.");
      setForm(emptyForm);
    } catch (err) {
      toast.error(
        "Something went wrong sending your enquiry. Please try again or email dream.entertainmentusa@gmail.com."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="booking-page">
      <PageHeader
        overline="Let's Make It Unforgettable"
        title="Book your"
        accent="date."
        description="Tell us about your celebration. The more detail, the better the soundtrack. We reply to every enquiry within 24 hours."
      />

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* STEPS */}
          <div className="lg:col-span-5 space-y-12">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div data-testid={`booking-step-${i + 1}`} className="flex gap-6">
                  <div className="shrink-0 w-14 h-14 rounded-full border border-primary/30 flex items-center justify-center text-primary">
                    <s.icon size={22} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-primary">
                      Step {i + 1}
                    </p>
                    <h3 className="font-serif text-2xl sm:text-3xl mt-1">{s.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {s.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <p className="text-sm text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-6">
                Prefer email? Write to us at{" "}
                <a
                  href="mailto:dream.entertainmentusa@gmail.com"
                  data-testid="booking-email-link"
                  className="text-primary hover:text-accent transition-colors duration-300"
                >
                  dream.entertainmentusa@gmail.com
                </a>
              </p>
            </Reveal>
          </div>

          {/* FORM */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="glass rounded-3xl p-8 sm:p-12 space-y-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    data-testid="booking-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full name"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="booking-phone-input"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(555) 123-4567"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="booking-email-input"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@email.com"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label>Event Type *</Label>
                  <Select
                    value={form.event_type}
                    onValueChange={(v) => setForm({ ...form, event_type: v })}
                  >
                    <SelectTrigger
                      data-testid="booking-event-type-select"
                      className={inputCls}
                    >
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          data-testid={`booking-event-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_date">Event Date *</Label>
                  <Input
                    id="event_date"
                    type="date"
                    data-testid="booking-date-input"
                    required
                    value={form.event_date}
                    onChange={set("event_date")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue / City</Label>
                  <Input
                    id="venue"
                    data-testid="booking-venue-input"
                    value={form.venue}
                    onChange={set("venue")}
                    placeholder="e.g. The Grand Ballroom, Edison NJ"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guests">Estimated Guests</Label>
                  <Input
                    id="guests"
                    data-testid="booking-guests-input"
                    value={form.guests}
                    onChange={set("guests")}
                    placeholder="e.g. 150"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label>Package</Label>
                  <Select
                    value={form.package}
                    onValueChange={(v) => setForm({ ...form, package: v })}
                  >
                    <SelectTrigger
                      data-testid="booking-package-select"
                      className={inputCls}
                    >
                      <SelectValue placeholder="Select a package" />
                    </SelectTrigger>
                    <SelectContent>
                      {packageOptions.map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          data-testid={`booking-package-option-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Budget</Label>
                  <Select
                    value={form.budget}
                    onValueChange={(v) => setForm({ ...form, budget: v })}
                  >
                    <SelectTrigger
                      data-testid="booking-budget-select"
                      className={inputCls}
                    >
                      <SelectValue placeholder="Select your budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map((b, i) => (
                        <SelectItem
                          key={b}
                          value={b}
                          data-testid={`booking-budget-option-${i}`}
                        >
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Add-Ons You&rsquo;re Interested In</Label>
                <div data-testid="booking-addons-group" className="flex flex-wrap gap-2.5">
                  {addonOptions.map((a) => {
                    const active = form.addons.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        data-testid={`booking-addon-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => toggleAddon(a)}
                        aria-pressed={active}
                        className={`rounded-full px-4 py-2 text-xs tracking-wide border transition-[background-color,color,border-color] duration-300 ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary"
                        }`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Tell Us About Your Celebration</Label>
                <Textarea
                  id="message"
                  data-testid="booking-message-input"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="The vibe you're dreaming of, must-play songs, special moments planned..."
                  rows={5}
                  className="bg-white/5 border-white/10 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50"
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={loading}
                className="w-full rounded-full bg-primary text-primary-foreground py-5 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_50px_rgba(255,0,127,0.4)] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-3"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Sending..." : "Send Enquiry"}
              </button>
              <Overline className="text-center !text-muted-foreground/60 normal-case tracking-widest">
                No spam. No pressure. Just a conversation about your big day.
              </Overline>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
